import { Injectable } from '@angular/core';

import { GraphEndpoint, HandleSimpleRequest, TaBaseService } from '@ta/server';
import { isNonNullable } from '@ta/utils';
import { filter, map, mergeMap } from 'rxjs';
import { createOwnerLink, deleteOwnerLink, updateOwner } from './mutation';
import { UpdateOwnerInput } from './form/dto/updateOwnerInput';
import { UserProfile, userProfileProps } from './dto/user';
import { ownerProps } from './dto/owner';
import { estateProps } from 'src/app/services/estates/dto/estate';
import { userInfoQuery } from './queries';

const graphEndpoint: GraphEndpoint = {
  clientName: 'ownerService',
  endpoint: '',
};

@Injectable({
  providedIn: 'root',
})
export class OwnerService extends TaBaseService {
  /** Le dossier propriétaire tel que le serveur le donne ; les écritures le font relire. */
  public ownerDetail = new HandleSimpleRequest<UserProfile>();

  constructor() {
    super();
    super.registerRoutes({ graphEndpoint: graphEndpoint });
  }

  public fetchOwnerDetails$() {
    return this.ownerDetail.fetch(
      this._graphService.fetchQueryBuilder<UserProfile>(
        userInfoQuery({
          props: `
            ${userProfileProps.get('id')}
            ${userProfileProps.get('bailoId')}
            ${userProfileProps.get('firstname')}
            ${userProfileProps.get('lastname')}
            ${userProfileProps.get('email')}
            ${userProfileProps.get('picture')}
            ${userProfileProps.get('dateOfBirth')}
            ${userProfileProps.get('owner')} {
              ${ownerProps.get('id')}
              ${ownerProps.get('description')}
              ${ownerProps.get('fieldVisibilities')} { fieldName isPublic }
              ${ownerProps.get('revenue')}
              ${ownerProps.get('estates')} {
                ${estateProps.get('id')}
                ${estateProps.get('name')}
                ${estateProps.get('description')}
                ${estateProps.get('status')}
                ${estateProps.get('available')}
                ${estateProps.get('floorArea')}
                ${estateProps.get('rooms')}
                ${estateProps.get('bedrooms')}
                ${estateProps.get('floor')}
                ${estateProps.get('serviceCharges')}
                ${estateProps.get('securityDeposit')}
                ${estateProps.get('insurance')}
                ${estateProps.get('createdDate')}
                ${estateProps.get('updatedDate')}
                ${estateProps.get('addressId')}
              }
            }
          `,
        }),
        graphEndpoint.clientName,
      ),
    );
  }

  public updateOwner$(owner: Partial<UpdateOwnerInput>) {
    return this._graphService
      .mutate(updateOwner(owner), 'updateOwner', graphEndpoint.clientName, ['userInfo'])
      .pipe(
        filter(isNonNullable),
        mergeMap((result) => this.fetchOwnerDetails$().pipe(map(() => result))),
      );
  }

  public createOwnerLink$() {
    return this._graphService
      .mutate<UserProfile>(createOwnerLink(), 'createOwnerLink', graphEndpoint.clientName, [
        'userInfo',
      ])
      .pipe(
        filter(isNonNullable),
        mergeMap((result) => this.fetchOwnerDetails$().pipe(map(() => result))),
      );
  }

  public deleteOwnerLink$() {
    return this._graphService
      .mutate<unknown>(deleteOwnerLink(), 'deleteOwnerLink', graphEndpoint.clientName, ['userInfo'])
      .pipe(
        filter(isNonNullable),
        mergeMap((result) => this.fetchOwnerDetails$().pipe(map(() => result))),
      );
  }
}
